import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Clock, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { scheduleSummaryLabel } from '@/frontend/features/HomeWork/format'
import type { LinkedRoutine, AvailableRoutine, ProcessDetailData } from './types'

interface Props {
  process: ProcessDetailData
  linkedRoutines: LinkedRoutine[]
  availableRoutines: AvailableRoutine[]
  routinesError?: string | null
}

/**
 * Painel lateral "Rotinas associadas" (Onda C) — e é AQUI que o elo
 * processo↔rotina (Entrega 1) vive na UI: um select das rotinas do agente
 * responsável para vincular, e um X para desvincular. Explícito e editável: o
 * dado do vínculo, apontado como inexistente na investigação (L1), passa a
 * EXISTIR por ação do usuário. Só aparece para quem pode gerir o processo.
 */
export function RoutinesSidePanel({ process, linkedRoutines, availableRoutines, routinesError }: Props) {
  const [selected, setSelected] = useState('')
  const [busy, setBusy] = useState(false)

  const link = () => {
    if (!selected) return
    setBusy(true)
    router.post(
      process.link_cron_path,
      { cron_id: selected },
      { preserveScroll: true, onFinish: () => { setBusy(false); setSelected('') } },
    )
  }

  const unlink = (cronId: string) => {
    setBusy(true)
    router.delete(process.unlink_cron_path, {
      data: { cron_id: cronId },
      preserveScroll: true,
      onFinish: () => setBusy(false),
    })
  }

  return (
    <section aria-label="Rotinas associadas" className="overflow-hidden rounded-xl border border-border bg-card">
      <header className="flex items-center gap-2 px-4 pb-1.5 pt-3">
        <Clock className="size-3.5 text-faint-foreground" aria-hidden />
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-faint-foreground">Rotinas associadas</h3>
        {linkedRoutines.length > 0 && (
          <span className="ml-auto rounded-full bg-secondary px-[7px] text-[10.5px] font-semibold text-muted-foreground">
            {linkedRoutines.length}
          </span>
        )}
      </header>

      {linkedRoutines.length === 0 ? (
        <p className="px-4 pb-3 pt-1 text-[13px] text-muted-foreground">Nenhuma rotina vinculada. Vincule uma pra automatizar este processo.</p>
      ) : (
        <ul>
          {linkedRoutines.map((routine) => (
            <li key={routine.id} className="flex items-center gap-2.5 border-t border-border px-4 py-2.5">
              <Clock className="size-3.5 flex-none text-faint-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-foreground">{routine.name}</p>
                <p className="truncate font-mono text-[11px] text-faint-foreground">{scheduleSummaryLabel(routine.schedule)}</p>
              </div>
              {process.can_manage && (
                <button
                  type="button"
                  onClick={() => unlink(routine.id)}
                  disabled={busy}
                  aria-label={`Desvincular ${routine.name}`}
                  className="flex-none rounded-md p-1 text-faint-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground disabled:opacity-50"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {process.can_manage && (
        <div className="border-t border-border px-4 py-3">
          {routinesError ? (
            <p className="text-[12.5px] text-muted-foreground">{routinesError}</p>
          ) : availableRoutines.length === 0 ? (
            <p className="text-[12.5px] text-faint-foreground">
              {linkedRoutines.length > 0
                ? 'Todas as rotinas do colaborador já estão vinculadas.'
                : 'Este colaborador ainda não tem rotinas.'}
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger size="sm" className="flex-1" aria-label="Escolher rotina para vincular">
                  <SelectValue placeholder="Vincular rotina…" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoutines.map((routine) => (
                    <SelectItem key={routine.id} value={routine.id}>{routine.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" size="sm" onClick={link} disabled={!selected || busy}>
                <Plus className="size-3.5" />
                Vincular
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
